import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLiveResultsContext } from '../context/LiveResultsContext';
import './liveCommentsBar.css';

// Wyróżnienia w treści — ta sama konwencja co panel admina (LiveComments.tsx):
// [tekst](https://adres) → link, #6 → załoga (tooltip z danymi), 12:34 → godzina.
const LINK_RE = /(\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g;

const renderTokens = (text, getEntryByNumber, keyPrefix) =>
  text.split(/(#\d+|\b\d{1,2}:\d{2}\b)/g).map((part, idx) => {
    const key = `${keyPrefix}-${idx}`;
    const crewMatch = part.match(/^#(\d+)$/);
    if (crewMatch) {
      const entry = getEntryByNumber ? getEntryByNumber(crewMatch[1]) : null;
      const tooltip = entry
        ? [entry.driverName, entry.coDriverName, entry.car].filter(Boolean).join(' / ')
        : null;
      return (
        <span key={key} className="live-bar__crew" title={tooltip || undefined}>
          #{crewMatch[1]}
        </span>
      );
    }
    if (/^\d{1,2}:\d{2}$/.test(part)) {
      return (
        <span key={key} className="live-bar__timechip">
          {part}
        </span>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });

export const renderCommentBody = (body, getEntryByNumber) =>
  String(body || '')
    .split(LINK_RE)
    .map((segment, i) => {
      const linkMatch = segment.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      if (linkMatch) {
        return (
          <a
            key={`l-${i}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {linkMatch[1]}
          </a>
        );
      }
      return renderTokens(segment, getEntryByNumber, `t-${i}`);
    });

const toHHMM = (dateTime) => {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const HISTORY_LIMIT = 5;

const LiveCommentsBar = ({ eventId, selectedCompetitionID, selectedStageID }) => {
  const { comments, seedComments, getEntryByNumber } = useLiveResultsContext();
  const [expanded, setExpanded] = useState(false);
  const historyRef = useRef(null);

  // Historia zapisana przed otwarciem strony — jednorazowy seed z REST;
  // kolejne komentarze przychodzą po WS (COMMENT_NEW/UPDATE/DELETE).
  useEffect(() => {
    if (!eventId || !seedComments) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URL}/api/public/liveComments?EventID=${eventId}`)
      .then((response) => (response.ok ? response.json() : []))
      .then((rows) => {
        if (!cancelled) seedComments(rows);
      })
      .catch((error) => console.error('Nie udało się pobrać komentarzy live', error));
    return () => {
      cancelled = true;
    };
  }, [eventId, seedComments]);

  // Widoczne: komentarze do całych zawodów (StageID null) + komentarze
  // aktualnie wybranego OS-u. Wpisy innych OS-ów nie zaśmiecają paska.
  const visibleComments = useMemo(() => {
    let list = comments;
    if (selectedCompetitionID) {
      list = list.filter((c) => Number(c.CompetitionID) === Number(selectedCompetitionID));
    }
    if (selectedStageID) {
      list = list.filter((c) => c.StageID == null || Number(c.StageID) === Number(selectedStageID));
    }
    return list;
  }, [comments, selectedCompetitionID, selectedStageID]);

  // Historia: 5 ostatnich, najstarszy u góry, najnowszy na dole.
  const historyComments = useMemo(
    () => visibleComments.slice(0, HISTORY_LIMIT).slice().reverse(),
    [visibleComments]
  );

  const hasComments = visibleComments.length > 0;

  // Strzałka "do góry" na mobile jest odsuwana nad pasek przez klasę na body.
  useEffect(() => {
    if (hasComments) document.body.classList.add('has-live-bar');
    return () => document.body.classList.remove('has-live-bar');
  }, [hasComments]);

  // Po rozwinięciu (i przy nowym komentarzu) przewiń historię na sam dół.
  useEffect(() => {
    const el = historyRef.current;
    if (expanded && el) el.scrollTop = el.scrollHeight;
  }, [expanded, historyComments]);

  if (!hasComments) return null;

  const latest = visibleComments[0];

  return (
    <>
      <div className="live-bar-spacer" />
      <div className="live-bar" translate="no">
        {expanded && (
          <div className="live-bar__history" ref={historyRef}>
            {historyComments.map((comment) => (
              <div className="live-bar__row" key={comment.ID}>
                <time>{toHHMM(comment.CommentTime)}</time>
                <span>{renderCommentBody(comment.Body, getEntryByNumber)}</span>
              </div>
            ))}
          </div>
        )}
        <div
          className="live-bar__toggle"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          onKeyDown={(e) => e.key === 'Enter' && setExpanded((open) => !open)}
        >
          <div className="live-bar__left">
            <div className="live-bar__dot" />
            <span className="live-bar__label">NA ŻYWO</span>
            <span className="live-bar__text">{renderCommentBody(latest.Body, getEntryByNumber)}</span>
            <span className="live-bar__time">{toHHMM(latest.CommentTime)}</span>
          </div>
          <span className="live-bar__expand">{expanded ? 'Zwiń ▲' : 'Więcej ▼'}</span>
        </div>
      </div>
    </>
  );
};

export default LiveCommentsBar;
